import { useState, useCallback } from 'react';
import { updateSetting } from '../api/settings';
import type { SettingDto } from '../types/settings';

interface UseUpdateSettingResult {
  update: (key: string, value: string) => Promise<SettingDto>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for updating a setting value
 */
export function useUpdateSetting(
  onSuccess?: (setting: SettingDto) => void
): UseUpdateSettingResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (key: string, value: string): Promise<SettingDto> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await updateSetting(key, { value });
        onSuccess?.(result);
        return result;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to update setting';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    update,
    isLoading,
    error,
    reset,
  };
}
